import { API_BASE_URL, ENDPOINTS, REQUEST_CONFIG } from './chatConfig';

/**
 * API методы для работы с чатом поддержки
 */

/**
 * Добавление сообщения в чат (REST API)
 * @param {Object} messageData - Данные сообщения
 * @param {string} messageData.orderId - ID заказа (опционально)
 * @param {string} messageData.email - Email пользователя (если нет orderId)
 * @param {string} messageData.phone - Телефон пользователя (если нет orderId)
 * @param {string} messageData.country - Страна пользователя (если нет orderId)
 * @param {string} messageData.state - Штат пользователя (если нет orderId)
 * @param {string} messageData.message - Текст сообщения
 * @param {boolean} messageData.isFromUser - От пользователя или агента
 * @returns {Promise<Object>} Ответ сервера
 */
export const addSupportMessage = async (messageData) => {
  try {
    const response = await fetch(`${API_BASE_URL}${ENDPOINTS.SUPPORT_MESSAGES}`, {
      method: 'POST',
      ...REQUEST_CONFIG,
      body: JSON.stringify(messageData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error adding support message:', error);
    throw error;
  }
};

/**
 * Получение сообщений по заказу
 * @param {string} orderId - ID заказа
 * @returns {Promise<Array>} Массив сообщений
 */
export const getOrderMessages = async (orderId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}${ENDPOINTS.SUPPORT_ORDER_MESSAGES(orderId)}`,
      {
        method: 'GET',
        ...REQUEST_CONFIG,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error fetching order messages:', error);
    throw error;
  }
};

/**
 * Получение заказа со всеми сообщениями
 * @param {string} orderId - ID заказа
 * @returns {Promise<Object>} Данные заказа с сообщениями
 */
export const getOrderWithMessages = async (orderId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}${ENDPOINTS.SUPPORT_ORDER(orderId)}`,
      {
        method: 'GET',
        ...REQUEST_CONFIG,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching order with messages:', error);
    throw error;
  }
};

/**
 * Получение последних чатов
 * @param {number} limit - Количество чатов
 * @returns {Promise<Array>} Массив чатов
 */
export const getRecentChats = async (limit = 50) => {
  try {
    const token = localStorage.getItem('agentToken');
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(
      `${API_BASE_URL}${ENDPOINTS.SUPPORT_RECENT_CHATS}?limit=${limit}`,
      {
        method: 'GET',
        headers: {
          ...REQUEST_CONFIG.headers,
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`);
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error fetching recent chats:', error);
    throw error;
  }
};

/**
 * Создание нового заказа
 * @param {Object} orderData - Данные заказа
 * @param {string} orderData.email - Email
 * @param {string} orderData.phone - Телефон
 * @param {string} orderData.country - Страна
 * @param {string} orderData.state - Штат
 * @returns {Promise<Object>} Созданный заказ
 */
export const createOrder = async (orderData) => {
  try {
    const response = await fetch(`${API_BASE_URL}${ENDPOINTS.ORDERS}`, {
      method: 'POST',
      ...REQUEST_CONFIG,
      body: JSON.stringify(orderData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

/**
 * Получение заказа по ID
 * @param {string} orderId - ID заказа
 * @returns {Promise<Object>} Данные заказа
 */
export const getOrder = async (orderId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}${ENDPOINTS.ORDER_BY_ID(orderId)}`,
      {
        method: 'GET',
        ...REQUEST_CONFIG,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching order:', error);
    throw error;
  }
};

/**
 * Получение сообщений заказа для агента (с авторизацией)
 * @param {string} orderId - ID заказа
 * @returns {Promise<Array>} Массив сообщений
 */
export const getOrderMessagesForAgent = async (orderId) => {
  try {
    const token = localStorage.getItem('agentToken');
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(
      `${API_BASE_URL}/support/orders/${orderId}/messages`,
      {
        method: 'GET',
        headers: {
          ...REQUEST_CONFIG.headers,
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`);
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error fetching order messages for agent:', error);
    throw error;
  }
};
